import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Orb } from './Orb';

/**
 * CommandPalette — the fastest door (⌘K / Ctrl-K from anywhere).
 *
 * Mirrors the Worker's GET /search shape: a flat hit is
 * { type, id, title, sub, route }. Safe *actions* arrive first with route: ''
 * — picking one POSTs /search/run, which enqueues the same guarded runner task
 * the matching cron runs (funnel-health, costs-rollup), then jumps to Tasks so
 * the founder watches the real receipt land. Every other hit navigates to
 * result.route. Styled per 04 (glass, 14px, ice glow, mono).
 */

type ResultType = 'action' | 'agent' | 'task' | 'approval' | 'message' | 'brain_node';

interface SearchResult {
  type: ResultType;
  id: string;
  title: string;
  sub: string;
  route: string;
}

/** Signal dot per type — a learned-once colour vocabulary, not a status. */
const DOT: Record<ResultType, string> = {
  action: 'var(--mint)', // safe to run
  agent: 'var(--ice)', // identity
  task: 'var(--amber)', // work in flight
  approval: 'var(--coral)', // needs your decision
  message: 'var(--dim)', // feed chatter
  brain_node: 'var(--violet)', // insight
};

/** JUMP = go to a place · OPEN = open the record · RUN·SAFE = enqueue a guarded action. */
const TAG: Record<ResultType, string> = {
  action: 'RUN · SAFE',
  agent: 'JUMP',
  task: 'OPEN',
  approval: 'OPEN',
  message: 'OPEN',
  brain_node: 'JUMP',
};

const DEBOUNCE_MS = 170;

export function CommandPalette() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const openRef = useRef(open);
  openRef.current = open;
  const reqId = useRef(0);

  // Global door: ⌘K / Ctrl-K toggles from anywhere (even inside inputs, since the
  // combo can't be confused with typing); Esc closes only when we're the one open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && openRef.current) {
        e.preventDefault();
        setOpen(false);
      }
    };
    // A mobile search affordance elsewhere can pop the palette via this event.
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('hq:open-palette', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('hq:open-palette', onOpen);
    };
  }, []);

  // On open: focus the input + lock the page behind the glass. On close: reset.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      inputRef.current?.focus();
      return () => {
        document.body.style.overflow = prev;
      };
    }
    setQ('');
    setResults([]);
    setActive(0);
    setLoading(false);
  }, [open]);

  // Debounced search. A monotonic reqId drops stale responses so fast typing
  // never flickers an older result set in.
  useEffect(() => {
    if (!open) return;
    const query = q.trim();
    if (!query) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const id = ++reqId.current;
    const t = setTimeout(() => {
      api
        .get<SearchResult[]>(`/search?q=${encodeURIComponent(query)}`)
        .then((rows) => {
          if (id !== reqId.current) return;
          setResults(rows);
          setActive(0);
        })
        .catch(() => {
          if (id === reqId.current) setResults([]);
        })
        .finally(() => {
          if (id === reqId.current) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, open]);

  // Auto-dismiss the action-result toast.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  function select(r: SearchResult | undefined) {
    if (!r) return;
    if (r.type === 'action' || !r.route) {
      // Fire the guarded action: the server (POST /search/run) re-checks the id
      // against its allowlist and enqueues the same task the cron runs. On
      // success we jump to Tasks so the founder watches the receipt land.
      void runAction(r);
      return;
    }
    setOpen(false);
    nav(r.route);
  }

  async function runAction(r: SearchResult) {
    try {
      const res = await api.post<{ task_id: string; title: string; deduped: boolean }>('/search/run', {
        id: r.id,
      });
      setOpen(false);
      setToast(res.deduped ? `Already running — ${r.title} · see Tasks` : `Started ${r.title} — watch it in Tasks`);
      nav('/tasks');
    } catch {
      setToast(`Couldn’t start ${r.title} — try again`);
    }
  }

  function onInputKey(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(results[active]);
    }
  }

  const showEmpty = !loading && q.trim().length > 0 && results.length === 0;

  return (
    <>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: 'clamp(48px, 13vh, 150px) 12px 16px',
            background: 'rgba(3, 8, 11, .55)',
            backdropFilter: 'blur(3px)',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 560,
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '70vh',
              background: 'var(--panel)',
              border: '1px solid var(--line-2)',
              borderRadius: 'var(--r-lg)',
              boxShadow: 'var(--glow-ice), 0 24px 60px rgba(0,0,0,.45)',
              backdropFilter: 'blur(24px)',
              overflow: 'hidden',
            }}
          >
            {/* input row */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '15px 18px',
                borderBottom: '1px solid var(--line)',
              }}
            >
              <span className="mono" aria-hidden style={{ color: 'var(--ice)', fontSize: 15 }}>
                ⌕
              </span>
              <input
                ref={inputRef}
                className="mono"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onInputKey}
                placeholder="Jump to an agent, task, approval, brain node — or run a safe action…"
                aria-label="Search the office"
                autoComplete="off"
                spellCheck={false}
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: 'var(--text)',
                  fontSize: 15,
                }}
              />
              <span
                className="mono"
                aria-hidden
                style={{
                  fontSize: 10,
                  letterSpacing: '.12em',
                  color: 'var(--faint)',
                  border: '1px solid var(--line-2)',
                  borderRadius: 999,
                  padding: '3px 8px',
                  flexShrink: 0,
                }}
              >
                ESC
              </span>
            </div>

            {/* results / states */}
            <div style={{ overflowY: 'auto', padding: 6 }}>
              {results.map((r, i) => {
                const on = i === active;
                return (
                  <button
                    key={`${r.type}:${r.id}`}
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => select(r)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      width: '100%',
                      textAlign: 'left',
                      background: on ? 'rgba(var(--ice-rgb), .10)' : 'transparent',
                      border: '1px solid',
                      borderColor: on ? 'var(--line-2)' : 'transparent',
                      borderRadius: 'var(--r-ctl)',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      color: 'var(--text)',
                      transition: 'background .12s, border-color .12s',
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: DOT[r.type],
                        boxShadow: on ? `0 0 8px ${DOT[r.type]}` : 'none',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          display: 'block',
                          fontWeight: 550,
                          fontSize: 13.5,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {r.title}
                      </span>
                      <span
                        className="mono"
                        style={{
                          display: 'block',
                          fontSize: 11,
                          color: 'var(--faint)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {r.sub}
                      </span>
                    </span>
                    <span
                      className="mono"
                      style={{
                        fontSize: 10,
                        letterSpacing: '.12em',
                        color: r.type === 'action' ? 'var(--mint)' : 'var(--dim)',
                        border: '1px solid',
                        borderColor: r.type === 'action' ? 'rgba(var(--mint-rgb), .4)' : 'var(--line-2)',
                        borderRadius: 999,
                        padding: '3px 9px',
                        flexShrink: 0,
                      }}
                    >
                      {TAG[r.type]}
                    </span>
                  </button>
                );
              })}

              {loading && results.length === 0 && (
                <p className="small" style={{ padding: '18px 12px' }}>
                  Searching…
                </p>
              )}

              {showEmpty && (
                <div style={{ textAlign: 'center', padding: '30px 16px 34px' }}>
                  <Orb agent="emma" size="md" />
                  <p className="dim" style={{ marginTop: 12, fontSize: 13.5 }}>
                    Nothing matches “{q.trim()}” yet — try a name, an order id, or “gauntlet”.
                  </p>
                </div>
              )}

              {!q.trim() && (
                <p className="small" style={{ padding: '18px 12px', color: 'var(--faint)' }}>
                  Search the whole office, or type “run” to see the safe actions you can enqueue.
                </p>
              )}
            </div>

            {/* footer hint */}
            <div
              className="mono"
              style={{
                display: 'flex',
                gap: 16,
                padding: '9px 16px',
                borderTop: '1px solid var(--line)',
                fontSize: 10,
                letterSpacing: '.06em',
                color: 'var(--faint)',
              }}
            >
              <span>↑↓ move</span>
              <span>↵ open</span>
              <span>esc close</span>
              <span style={{ marginLeft: 'auto' }}>⌘K anywhere</span>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 24,
            transform: 'translateX(-50%)',
            zIndex: 1100,
          }}
        >
          <div className="toast">
            <span aria-hidden style={{ color: 'var(--mint)' }}>✓</span>
            <span>{toast}</span>
          </div>
        </div>
      )}
    </>
  );
}
